import './index.scss'

import planningCenterLogoWhite from 'url:@image/planning-center-white.svg'
import planningCenterLogoColor from 'url:@image/planning-center-full-color.svg'

import smartWaiverLogo from 'url:@image/smartwaiver-logo.svg'

import useDarkMode from '@hooks/useDarkMode'
import { useEffect, useState } from 'react'
import { useAuth } from '@auth/AuthProvider'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import IntegrationsController from '@data/controller/IntegrationsController'
import { ExternalVendor } from '@interfaces/external-vendor.interface'
import {
  IntegrationAuthorizationStyle,
  IntegrationConnection,
  IntegrationStatus
} from '@interfaces/integration.interface'
import LoadingIndicator from '@components/LoadingIndicator'
import Modal, { ModalButtonStyle } from '@components/Modal'
import PageTabs from '@components/PageTabs'
import DisconnectModal from './DisconnectModal'

type ProviderConfig = {
  provider: ExternalVendor;
  name: string;
  description: string;
  authorizationStyle: IntegrationAuthorizationStyle;
  /// How a church finds their key. The API has no route for this -- an API-key
  /// provider has nothing to redirect to -- so the copy lives here.
  apiKeyInstructions?: string;
  /// What stops working on disconnect, for the warning shown before it.
  disconnectConsequences: string;
};

const PROVIDERS: ProviderConfig[] = [
  {
    provider: ExternalVendor.PLANNING_CENTER,
    name: 'Planning Center',
    description: 'Connecting with Planning Center lets your church bring its people, volunteer teams, and event registrations into Salt.',
    authorizationStyle: IntegrationAuthorizationStyle.OAUTH,
    disconnectConsequences: 'Planning Center registrations will stop syncing with Salt Events, and you won\'t be able to import people or volunteer teams from Planning Center. People and teams you\'ve already imported will stay in Salt.'
  },
  {
    provider: ExternalVendor.SMART_WAIVER,
    name: 'Smartwaiver',
    description: 'Connecting with Smartwaiver allows completed waivers to be automatically linked to event registrations in Salt Events.',
    authorizationStyle: IntegrationAuthorizationStyle.API_KEY,
    disconnectConsequences: 'Completed waivers will stop being linked to event registrations in Salt Events, and your Smartwaiver API key will be removed from Salt.',
    apiKeyInstructions: `1. On <a target="_blank" rel="noopener noreferrer" href="https://app.smartwaiver.com/console">Smartwaiver's website</a>, navigate to the "My Account" tab.\n
2. Click "API Keys".\n
3. Give the key a name. We suggest "Salt Church Hub" or "Salt".\n
4. Ensure the "API User" box is checked.\n
5. Click "Save API Token".\n
6. Click "Copy key".\n
7. Return to this page, and paste the key below.`
  }
];

export default function Connections() {
  const auth = useAuth();
  const darkMode = useDarkMode();

  const { integrations, reloadConnectionStatus } = useChurchDataProvider();
  const [busyProvider, setBusyProvider] = useState<ExternalVendor | null>(null);

  const [showAPIKeyModal, setShowAPIKeyModal] = useState(false);
  const [savingAPIKey, setSavingAPIKey] = useState(false);
  const [apiKeyProvider, setAPIKeyProvider] = useState<ProviderConfig | null>(null);

  // The target outlives the open flag so the modal keeps its copy while it
  // animates closed.
  const [disconnectTarget, setDisconnectTarget] = useState<ProviderConfig | null>(null);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const openDisconnectModal = (config: ProviderConfig) => {
    setDisconnectTarget(config);
    setDisconnectError(null);
    setIsDisconnectModalOpen(true);
  };

  const closeDisconnectModal = () => {
    setIsDisconnectModalOpen(false);

    window.setTimeout(() => {
      setDisconnectTarget(null);
    }, 200);
  };

  const connectionFor = (provider: ExternalVendor) => {
    return integrations?.find((connection) => connection.provider === provider) ?? null;
  };

  const connect = async (config: ProviderConfig) => {
    // An API-key provider has nothing to redirect to: the church pastes a key
    // in instead, and the API answers its authorization-url route with a 400.
    if (config.authorizationStyle === IntegrationAuthorizationStyle.API_KEY) {
      setAPIKeyProvider(config);
      return;
    }

    if (!auth.churchId || busyProvider) return;

    setBusyProvider(config.provider);

    try {
      const response = await IntegrationsController.fetchAuthorizationURL(auth.churchId, config.provider, auth);
      const authorizationURL = response.body?.result?.authorizationURL;

      if (response.error || !authorizationURL) {
        window.alert(response.error || `Failed to start connecting with ${config.name}. Please try again later.`);
        return;
      }

      window.location.href = authorizationURL;
    } finally {
      setBusyProvider(null);
    }
  };

  const saveAPIKeyForSelectedProvider = async (apiKey: string) => {
    if (!auth.churchId || !apiKeyProvider) return;
    setSavingAPIKey(true);

    try {
      const response = await IntegrationsController.saveAPIKey(
        auth.churchId,
        apiKeyProvider.provider,
        apiKey,
        auth
      );

      if (response.error) {
        window.alert(response.error);
        console.log(response.error);
      } else {
        setShowAPIKeyModal(false);
        reloadConnectionStatus();
      }
    } catch {
      window.alert("Failed to save API Key. Please try again later.");
    } finally {
      setSavingAPIKey(false);
    }
  };

  const disconnect = async () => {
    if (!auth.churchId || !disconnectTarget) return;
    setIsDisconnecting(true);
    setDisconnectError(null);

    try {
      const response = await IntegrationsController.disconnect(auth.churchId, disconnectTarget.provider, auth);

      if (response.error) {
        setDisconnectError(response.error);
        return;
      }

      closeDisconnectModal();
      reloadConnectionStatus();
    } finally {
      setIsDisconnecting(false);
    }
  };

  useEffect(() => {
    // Ensure we have the latest connection status when this page mounts
    reloadConnectionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (apiKeyProvider !== null) {
      setShowAPIKeyModal(true);
    }
  }, [apiKeyProvider]);

  useEffect(() => {
    if (!showAPIKeyModal) {
      setTimeout(() => {
        setAPIKeyProvider(null);
      }, 400);
    }
  }, [showAPIKeyModal]);

  if (!integrations) {
    return <div className='connections-container'>
      <LoadingIndicator />
    </div>
  }

  return <div className='connections-container'>
    <PageTabs title="Connections"
      tabs={[]}
      buttons={[]} />

    <APIKeyConnectionInstructionsModal isOpen={showAPIKeyModal}
      config={apiKeyProvider}
      close={() => {
        setShowAPIKeyModal(false);
      }}
      save={(apiKey) => {
        saveAPIKeyForSelectedProvider(apiKey);
      }}
      isSaving={savingAPIKey} />

    <DisconnectModal
      isOpen={isDisconnectModalOpen}
      providerName={disconnectTarget?.name}
      consequences={disconnectTarget?.disconnectConsequences}
      isDisconnecting={isDisconnecting}
      error={disconnectError}
      close={closeDisconnectModal}
      confirm={() => {
        void disconnect();
      }} />

    <div className='cards'>
      {PROVIDERS.map((config) => (
        <ConnectionCard
          key={config.provider}
          config={config}
          logo={config.provider === ExternalVendor.PLANNING_CENTER ?
            (darkMode ? planningCenterLogoWhite : planningCenterLogoColor) :
            smartWaiverLogo}
          connection={connectionFor(config.provider)}
          isBusy={busyProvider === config.provider}
          connectButtonClicked={() => {
            void connect(config);
          }}
          disconnectButtonClicked={() => openDisconnectModal(config)}
        />
      ))}
    </div>
  </div>
}

// Only an active connection is usable; the others each need the church to do
// something, so they say what rather than reading as connected.
function statusLabel(connection: IntegrationConnection) {
  switch (connection.status) {
    case IntegrationStatus.ACTIVE:
      return 'Connected';
    case IntegrationStatus.NEEDS_REAUTHORIZATION:
      return 'Needs reauthorization';
    case IntegrationStatus.REVOKED:
      return 'Disconnected by provider';
    default:
      return 'Connected';
  }
}

function ConnectionCard(props: {
  config: ProviderConfig,
  logo: any,
  connection: IntegrationConnection | null,
  isBusy: boolean,
  connectButtonClicked: () => void,
  disconnectButtonClicked: () => void
}) {
  const connection = props.connection;
  const isActive = connection?.status === IntegrationStatus.ACTIVE;
  const isAPIKey = props.config.authorizationStyle === IntegrationAuthorizationStyle.API_KEY;

  return <div className='connection-card card'>
    <div className='header'>
      <img src={props.logo} alt={props.config.name} />
    </div>

    <p className='description'>{props.config.description}</p>

    {connection ? <>
      <p className={isActive ? 'connected-text' : 'connected-text needs-attention'}>
        <span className='connected-dot' aria-hidden="true" /> {statusLabel(connection)}
      </p>

      {connection.organizationName ? <p className='connection-detail'>{connection.organizationName}</p> : null}

      <div className='connection-actions'>
        {!isActive ? <button onClick={props.connectButtonClicked} disabled={props.isBusy}>
          {isAPIKey ? 'Update API Key' : 'Reconnect'}
        </button> : null}

        <button className='secondary' onClick={props.disconnectButtonClicked} disabled={props.isBusy}>
          Disconnect
        </button>
      </div>
    </> : <button onClick={props.connectButtonClicked} disabled={props.isBusy}>
      Connect
    </button>}
  </div>
}

function APIKeyConnectionInstructionsModal(props: {
  isOpen: boolean,
  config: ProviderConfig | null,
  close: () => void,
  save: (apiKey: string) => void,
  isSaving: boolean
}) {
  const [apiKey, setAPIKey] = useState('');

  return <Modal id="apiKey" isOpen={props.isOpen} footerButtons={[
    {
      title: 'Cancel',
      style: ModalButtonStyle.OUTLINE,
      onClick: props.close,
      enabled: !props.isSaving
    },
    {
      title: 'Save',
      style: ModalButtonStyle.FILLED,
      onClick: () => {
        props.save(apiKey);
      },
      enabled: apiKey.length > 0 && !props.isSaving,
    }
  ]}>
    {props.config?.apiKeyInstructions ?
      <div style={{ padding: '4px 0 16px 0' }}>
        <h3>Instructions</h3>
        <p dangerouslySetInnerHTML={{ __html: props.config.apiKeyInstructions.replace(/\n/g, '<br />') }}></p>

        <br />

        <input id='apiKey' type="text" placeholder='API Key' style={{
          width: '100%',
          boxSizing: 'border-box',
          height: '48px',
          border: 'none',
          borderRadius: '8px',
          paddingLeft: '16px',
          fontFamily: 'monospace',
        }}
          value={apiKey}
          onChange={(e) => {
            setAPIKey(e.target.value);
          }} />
      </div>
      : null}
  </Modal>
}
